// Live social presence over HTTP polling — the web port of the app's presence
// loop (react-native/src/presence.js). POST /presence refreshes my heartbeat
// (making me "online" to friends), shares my location while recording a trip,
// and returns friends' live positions + pending trip invites in one call.
//
// setTimeout-chained (not setInterval) so a slow request can't stack up calls;
// pauses while the tab is hidden and refreshes immediately on return.

import { WEB_DEFAULT_SETTINGS } from "./web_settings"
import { WEB_PRESENCE_POLICY } from "./presence_policy"
import { responseErrorCode, tripStateTerminal } from "./tracker_remote_policy"
import { apiFetch } from "./api"
import { logEvent } from "./client_logger"
import { projectFriendState } from "./realtime_contract"
import { sanitizeSpeedMps } from "./web_location_policy"

export function presenceHeartbeatBody({ tracking = false, tripId = null, last = null, speed = null, heading = null, paused = false } = {}) {
  if (!tracking || !tripId) return {}

  // Paused trip: keep the claim alive (so the trip isn't abandoned) but
  // withhold the frozen coordinates — friends must not see a "live" position
  // that stopped moving when the user paused.
  if (paused) return { trip_id: Number(tripId) }

  if (!last) return {}

  return {
    lat: last[0],
    lon: last[1],
    speed: sanitizeSpeedMps(speed),
    heading: heading ?? null,
    trip_id: Number(tripId)
  }
}

export function presencePollIntervalSec({ tracking = false, tripId = null, locationIntervalSec = WEB_DEFAULT_SETTINGS.locationIntervalSec } = {}) {
  if (!tracking || !tripId) return WEB_PRESENCE_POLICY.offTripIntervalSec
  const intervalSec = Number(locationIntervalSec) || WEB_DEFAULT_SETTINGS.locationIntervalSec
  return Math.min(intervalSec, WEB_PRESENCE_POLICY.trackingIntervalMaxSec)
}

export function presenceRetryDelaySec({ intervalSec, failures = 0, random = Math.random } = {}) {
  const base = Math.max(WEB_PRESENCE_POLICY.minIntervalSec, Number(intervalSec) || WEB_PRESENCE_POLICY.minIntervalSec)
  if (failures <= 0) return Math.min(base, WEB_PRESENCE_POLICY.failureBackoffMaxSec)
  const exponential = Math.min(base * (2 ** failures), WEB_PRESENCE_POLICY.failureBackoffMaxSec)
  const jitter = 1 + ((Number(random()) || 0) * 2 - 1) * WEB_PRESENCE_POLICY.failureJitterRatio
  return Math.max(
    WEB_PRESENCE_POLICY.minIntervalSec,
    Math.min(exponential * jitter, WEB_PRESENCE_POLICY.failureBackoffMaxSec)
  )
}

export function createPresencePoller({ url, body, intervalSec, onData, onStale, onTripStateTerminal, random = Math.random }) {
  let timer = null
  let stopped = false
  let gen = 0
  let failures = 0
  let lastFreshAt = null
  let lastWarningAt = null

  async function tick(g) {
    if (document.visibilityState === "hidden") return
    try {
      const res = await apiFetch(url, {
        method: "POST",
        body: JSON.stringify(body())
      })
      if (!res.ok) {
        // A terminal trip-state rejection (the claimed trip ended elsewhere) is
        // not an outage: report it so the tracker can wind down, and don't let
        // it count toward the stale-failures budget that blanks the map.
        if (tripStateTerminal(await responseErrorCode(res))) {
          if (!stopped && g === gen) onTripStateTerminal?.()
          return
        }
        throw new Error(`HTTP ${res.status}`)
      }
      const data = await res.json()
      if (stopped || g !== gen) return
      failures = 0
      lastFreshAt = Date.now()
      onData({
        ...data,
        friends: Array.isArray(data?.friends) ? data.friends.map(projectFriendState) : []
      })
    } catch (error) {
      if (stopped || g !== gen) return
      // Transient failures keep the last-known friends; only a sustained outage
      // goes stale, so one dropped request doesn't blank the map.
      failures += 1
      const now = Date.now()
      const warningAge = lastWarningAt == null ? Infinity : now - lastWarningAt
      if (warningAge < 0 || warningAge >= WEB_PRESENCE_POLICY.staleAfterMs) {
        lastWarningAt = now
        logEvent("presence.heartbeat_failed", error, {
          failures,
          reason: error instanceof Error ? error.message : String(error)
        })
      }
      const age = lastFreshAt == null ? Infinity : now - lastFreshAt
      if (failures >= WEB_PRESENCE_POLICY.failuresBeforeStale && age >= WEB_PRESENCE_POLICY.staleAfterMs) onStale?.()
    }
  }

  async function loop(g) {
    if (stopped || g !== gen || document.visibilityState === "hidden") return
    await tick(g)
    if (stopped || g !== gen || document.visibilityState === "hidden") return
    const sec = presenceRetryDelaySec({ intervalSec: intervalSec(), failures, random })
    timer = setTimeout(() => loop(g), sec * 1000)
  }

  // Restart the chain now — used when the trip state flips (share/stop sharing
  // promptly, retune cadence) and when the tab becomes visible again.
  function kick() {
    gen += 1
    clearTimeout(timer)
    timer = null
    loop(gen)
  }

  // Invalidate the current generation on hide so an in-flight request cannot
  // recreate the timer chain after the tab has been backgrounded.
  function pause() {
    gen += 1
    clearTimeout(timer)
    timer = null
  }

  const visHandler = () => {
    if (document.visibilityState === "visible") kick()
    else pause()
  }
  document.addEventListener("visibilitychange", visHandler)
  kick()

  return {
    kick,
    stop() {
      stopped = true
      clearTimeout(timer)
      document.removeEventListener("visibilitychange", visHandler)
    }
  }
}
